import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Phone,
  Mail,
  User,
  IndianRupee,
  Users,
  Building2,
  FileText,
  AlertTriangle,
  Receipt,
  Percent,
  Save,
  Check
} from "lucide-react";
import MobileDatePickerModal from "@/components/common/MobileDatePickerModal";
import MobileTimePickerModal, { formatTime12 } from "@/components/common/MobileTimePickerModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const EVENT_TYPES = [
  "Wedding",
  "Reception",
  "Engagement",
  "Sangeet / Mehendi",
  "Birthday Party",
  "Corporate Event / Conference",
  "Anniversary",
  "Thread Ceremony / Upanayana",
  "Exhibition / Trade Show",
  "Other Celebration"
];

export default function EditBookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [conflictWarning, setConflictWarning] = useState("");

  // Modals for Custom Pickers
  const [activeDateModal, setActiveDateModal] = useState(null); // "start" or "end"
  const [activeTimeModal, setActiveTimeModal] = useState(null); // "start" or "end"

  const [editForm, setEditForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    event_type: "Wedding",
    start_date: "",
    end_date: "",
    start_time: "06:00",
    end_time: "16:59",
    guest_count: 300,
    hall_section: "Main Grand Hall",
    notes: "",
    special_requirements: "",
    // Financial Details
    hall_rent: 0,
    labor_charges: 0,
    electricity_charges: 0,
    maintenance_fee: 0,
    apply_gst: false,
    additional_charges: [
      { name: "", amount: "" },
      { name: "", amount: "" },
      { name: "", amount: "" }
    ],
    advance_paid: 0
  });

  useEffect(() => {
    const loadBooking = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/bookings/${id}`);
        const b = res.data;
        const existingAdd = b.additional_charges || [];
        const addSlots = [
          existingAdd[0] || { name: "", amount: "" },
          existingAdd[1] || { name: "", amount: "" },
          existingAdd[2] || { name: "", amount: "" }
        ];

        setEditForm({
          client_name: b.client_name || "",
          client_phone: b.client_phone || "",
          client_email: b.client_email || "",
          event_type: b.event_type || "Wedding",
          start_date: b.start_date || "",
          end_date: b.end_date || "",
          start_time: b.start_time || "06:00",
          end_time: b.end_time || "16:59",
          guest_count: b.guest_count || 300,
          hall_section: b.hall_section || "Main Grand Hall",
          notes: b.notes || "",
          special_requirements: b.special_requirements || "",
          hall_rent: b.hall_rent !== undefined ? b.hall_rent : (b.total_amount || 0),
          labor_charges: b.labor_charges || 0,
          electricity_charges: b.electricity_charges || 0,
          maintenance_fee: b.maintenance_fee || 0,
          apply_gst: Boolean(b.apply_gst),
          additional_charges: addSlots,
          advance_paid: b.advance_paid || 0
        });
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    loadBooking();
  }, [id]);

  const handleAdditionalChargeChange = (index, field, value) => {
    setEditForm((prev) => {
      const updated = [...prev.additional_charges];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, additional_charges: updated };
    });
  };

  // Financial Calculations
  const editHallRent = parseFloat(editForm.hall_rent) || 0;
  const editLabor = parseFloat(editForm.labor_charges) || 0;
  const editElectricity = parseFloat(editForm.electricity_charges) || 0;
  const editMaintenance = parseFloat(editForm.maintenance_fee) || 0;
  const editSgst = editForm.apply_gst ? editHallRent * 0.09 : 0;
  const editCgst = editForm.apply_gst ? editHallRent * 0.09 : 0;
  const editTotalGst = editSgst + editCgst;
  const editAdditionalTotal = (editForm.additional_charges || []).reduce((acc, curr) => {
    return acc + (parseFloat(curr.amount) || 0);
  }, 0);
  const editTotalAmount = editHallRent + editLabor + editElectricity + editMaintenance + editTotalGst + editAdditionalTotal;
  const editAdvance = parseFloat(editForm.advance_paid) || 0;
  const editBalanceDue = Math.max(0, editTotalAmount - editAdvance);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setConflictWarning("");

    const startIso = `${editForm.start_date}T${editForm.start_time}:00+00:00`;
    const endIso = `${editForm.end_date}T${editForm.end_time}:00+00:00`;

    try {
      const checkRes = await axios.post(`${API}/availability/check`, {
        start_datetime_iso: startIso,
        end_datetime_iso: endIso,
        exclude_booking_id: id
      });

      if (!checkRes.data.available) {
        setConflictWarning(checkRes.data.message || "Date/Time Unavailable: Overlaps with another booking.");
        setIsUpdating(false);
        return;
      }

      const filteredAddCharges = (editForm.additional_charges || [])
        .filter((c) => c.name.trim() !== "" || parseFloat(c.amount) > 0)
        .map((c) => ({
          name: c.name.trim() || "Additional Fee",
          amount: parseFloat(c.amount) || 0
        }));

      await axios.put(`${API}/bookings/${id}`, {
        clientName: editForm.client_name,
        customer_name: editForm.client_name,
        client_name: editForm.client_name,
        phone: editForm.client_phone,
        customer_phone: editForm.client_phone,
        client_phone: editForm.client_phone,
        email: editForm.client_email,
        customer_email: editForm.client_email,
        client_email: editForm.client_email,
        event_type: editForm.event_type,
        eventDate: editForm.start_date,
        date: editForm.start_date,
        start_date: editForm.start_date,
        endDate: editForm.end_date,
        end_date: editForm.end_date,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        start_datetime_iso: startIso,
        end_datetime_iso: endIso,
        guest_count: parseInt(editForm.guest_count) || 0,
        hall_section: editForm.hall_section,
        notes: editForm.notes,
        special_requirements: editForm.special_requirements,
        // Financials
        hall_rent: editHallRent,
        labor_charges: editLabor,
        electricity_charges: editElectricity,
        maintenance_fee: editMaintenance,
        apply_gst: editForm.apply_gst,
        sgst_amount: editSgst,
        cgst_amount: editCgst,
        additional_charges: filteredAddCharges,
        totalAmount: editTotalAmount,
        total_rent: editTotalAmount,
        total_amount: editTotalAmount,
        advancePaid: editAdvance,
        deposit_amount: editAdvance,
        advance_paid: editAdvance
      });

      navigate(`/bookings/${id}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to update booking.";
      setConflictWarning(detail);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-pink-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to={`/bookings/${id}`}
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Booking Details</span>
        </Link>

        <span className="text-xs font-semibold text-gray-500">
          Edit Mode
        </span>
      </div>

      {/* Main Form Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
            Edit Booking & Schedule
          </h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1">
            Update event dates, function hours, client contact and financial charges with real-time overlap checking.
          </p>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-6 sm:p-8 space-y-6 bg-gray-50/30">
          
          {conflictWarning && (
            <div className="p-4 bg-rose-50 border-2 border-rose-400 rounded-2xl text-xs sm:text-sm text-rose-950 flex items-start gap-2.5 animate-shake">
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <span className="font-bold">{conflictWarning}</span>
            </div>
          )}

          {/* Schedule & Timings */}
          <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
              <Clock className="w-4 h-4 text-pink-700" />
              <span>Event Schedule & Timings</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Start Date *</label>
                <button
                  type="button"
                  data-testid="edit-booking-start-date"
                  onClick={() => setActiveDateModal("start")}
                  className="w-full px-4 py-3.5 rounded-2xl border-2 border-pink-200 bg-white hover:bg-pink-50/50 text-pink-950 font-bold text-sm flex items-center justify-between shadow-xs transition-all text-left group"
                >
                  <div className="flex items-center gap-2.5">
                    <Calendar className="w-4 h-4 text-pink-700 flex-shrink-0" />
                    <span className="text-sm font-black">{editForm.start_date || "Select Date"}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-pink-600 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">End Date *</label>
                <button
                  type="button"
                  data-testid="edit-booking-end-date"
                  onClick={() => setActiveDateModal("end")}
                  className="w-full px-4 py-3.5 rounded-2xl border-2 border-pink-200 bg-white hover:bg-pink-50/50 text-pink-950 font-bold text-sm flex items-center justify-between shadow-xs transition-all text-left group"
                >
                  <div className="flex items-center gap-2.5">
                    <Calendar className="w-4 h-4 text-pink-700 flex-shrink-0" />
                    <span className="text-sm font-black">{editForm.end_date || editForm.start_date || "Select Date"}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-pink-600 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Start Time (AM/PM) *</label>
                <button
                  type="button"
                  data-testid="edit-booking-start-time"
                  onClick={() => setActiveTimeModal("start")}
                  className="w-full px-4 py-3.5 rounded-2xl border-2 border-pink-200 bg-white hover:bg-pink-50/50 text-pink-950 font-bold text-sm flex items-center justify-between shadow-xs transition-all text-left group"
                >
                  <div className="flex items-center gap-2.5">
                    <Clock className="w-4 h-4 text-pink-700 flex-shrink-0" />
                    <span className="text-sm font-black">{formatTime12(editForm.start_time)}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-pink-600 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">End Time (AM/PM) *</label>
                <button
                  type="button"
                  data-testid="edit-booking-end-time"
                  onClick={() => setActiveTimeModal("end")}
                  className="w-full px-4 py-3.5 rounded-2xl border-2 border-pink-200 bg-white hover:bg-pink-50/50 text-pink-950 font-bold text-sm flex items-center justify-between shadow-xs transition-all text-left group"
                >
                  <div className="flex items-center gap-2.5">
                    <Clock className="w-4 h-4 text-pink-700 flex-shrink-0" />
                    <span className="text-sm font-black">{formatTime12(editForm.end_time)}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-pink-600 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
              <User className="w-4 h-4 text-pink-700" />
              <span>Customer Information</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  required
                  value={editForm.client_name}
                  onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={editForm.client_phone}
                  onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Event Type *</label>
                <select
                  value={editForm.event_type}
                  onChange={(e) => setEditForm({ ...editForm, event_type: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm bg-white"
                >
                  {EVENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Expected Guests</label>
                <input
                  type="number"
                  value={editForm.guest_count}
                  onChange={(e) => setEditForm({ ...editForm, guest_count: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-700 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Financial Details */}
          <div className="bg-white p-5 rounded-2xl border border-pink-100 shadow-sm space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-pink-700" />
              <span>Financial Charges (INR ₹)</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Hall Rent (₹)</label>
                <input
                  data-testid="edit-input-hall-rent"
                  type="number"
                  value={editForm.hall_rent}
                  onChange={(e) => setEditForm({ ...editForm, hall_rent: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-sm font-bold text-pink-950"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Labor Charges (₹)</label>
                <input
                  data-testid="edit-input-labor-charges"
                  type="number"
                  value={editForm.labor_charges}
                  onChange={(e) => setEditForm({ ...editForm, labor_charges: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Electricity Charges (₹)</label>
                <input
                  data-testid="edit-input-electricity-charges"
                  type="number"
                  value={editForm.electricity_charges}
                  onChange={(e) => setEditForm({ ...editForm, electricity_charges: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Maintenance Fee (₹)</label>
                <input
                  data-testid="edit-input-maintenance-fee"
                  type="number"
                  value={editForm.maintenance_fee}
                  onChange={(e) => setEditForm({ ...editForm, maintenance_fee: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-pink-200 text-sm"
                />
              </div>
            </div>

            {/* 18% GST */}
            <div className="p-4 rounded-2xl bg-pink-50 border border-pink-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-pink-950 block">Apply 18% GST</span>
                <span className="text-[11px] text-gray-500">9% SGST + 9% CGST on Hall Rent only</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={editForm.apply_gst}
                data-testid="edit-switch-apply-gst"
                onClick={() => setEditForm((prev) => ({ ...prev, apply_gst: !prev.apply_gst }))}
                className={`w-11 h-6 rounded-full transition-colors relative focus:outline-none flex items-center p-0.5 ${
                  editForm.apply_gst ? "bg-pink-800" : "bg-gray-300"
                }`}
              >
                <span
                  className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    editForm.apply_gst ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {editForm.apply_gst && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 p-2.5 rounded-xl text-gray-700 flex justify-between">
                  <span>SGST (9%):</span>
                  <span className="font-bold">₹{editSgst.toLocaleString("en-IN")}</span>
                </div>
                <div className="bg-gray-50 p-2.5 rounded-xl text-gray-700 flex justify-between">
                  <span>CGST (9%):</span>
                  <span className="font-bold">₹{editCgst.toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}

            {/* Additional Charges */}
            <div className="space-y-2 pt-1">
              <span className="text-xs font-bold text-gray-700 block">3 Additional Charges (Optional)</span>
              {[0, 1, 2].map((idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input
                    type="text"
                    value={editForm.additional_charges[idx]?.name || ""}
                    onChange={(e) => handleAdditionalChargeChange(idx, "name", e.target.value)}
                    placeholder={`Charge ${idx + 1} Name`}
                    className="col-span-7 px-3.5 py-2 rounded-xl border border-pink-200 text-xs"
                  />
                  <input
                    type="number"
                    value={editForm.additional_charges[idx]?.amount || ""}
                    onChange={(e) => handleAdditionalChargeChange(idx, "amount", e.target.value)}
                    placeholder="₹ Amount"
                    className="col-span-5 px-3.5 py-2 rounded-xl border border-pink-200 text-xs font-mono"
                  />
                </div>
              ))}
            </div>

            {/* Totals Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-pink-100 text-center">
              <div className="p-3 bg-pink-100 rounded-2xl">
                <span className="text-xs text-pink-900 block font-bold">Total Amount</span>
                <span className="text-base font-black font-mono">₹{editTotalAmount.toLocaleString("en-IN")}</span>
              </div>
              <div>
                <label className="text-xs text-gray-600 block font-semibold mb-1">Advance Paid (₹)</label>
                <input
                  data-testid="edit-input-advance-paid"
                  type="number"
                  value={editForm.advance_paid}
                  onChange={(e) => setEditForm({ ...editForm, advance_paid: e.target.value })}
                  className="w-full text-center py-2 rounded-xl border border-pink-200 text-sm font-bold text-emerald-800"
                />
              </div>
              <div className="p-3 bg-rose-50 rounded-2xl">
                <span className="text-xs text-rose-900 block font-bold">Balance Due</span>
                <span className="text-base font-black text-rose-900 font-mono">₹{editBalanceDue.toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>

          {/* Submit Actions */}
          <div className="pt-4 border-t border-pink-100 flex items-center justify-end gap-3">
            <Link
              to={`/bookings/${id}`}
              className="px-5 py-3 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-100 text-sm font-semibold transition-colors"
            >
              Cancel
            </Link>
            <button
              data-testid="btn-save-booking-edit"
              type="submit"
              disabled={isUpdating}
              className="px-8 py-3 rounded-xl bg-gradient-to-r from-pink-900 via-pink-800 to-rose-700 text-white font-bold text-sm shadow-lg hover:opacity-95 active:scale-95 transition-all flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              <span>{isUpdating ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Mobile App Style Date Picker Modal */}
      <MobileDatePickerModal
        isOpen={Boolean(activeDateModal)}
        onClose={() => setActiveDateModal(null)}
        selectedDate={activeDateModal === "end" ? (editForm.end_date || editForm.start_date) : editForm.start_date}
        title={activeDateModal === "end" ? "Select End Date" : "Select Start Date"}
        onSelectDate={(date) => {
          if (activeDateModal === "start") {
            setEditForm((prev) => ({
              ...prev,
              start_date: date,
              end_date: prev.start_date === prev.end_date || !prev.end_date || prev.end_date < date ? date : prev.end_date
            }));
          } else if (activeDateModal === "end") {
            setEditForm((prev) => ({ ...prev, end_date: date }));
          }
          setActiveDateModal(null);
        }}
      />

      {/* Mobile App Style Round Analog Clock Time Picker Modal */}
      <MobileTimePickerModal
        isOpen={Boolean(activeTimeModal)}
        onClose={() => setActiveTimeModal(null)}
        initialTime={activeTimeModal === "end" ? editForm.end_time : editForm.start_time}
        title={activeTimeModal === "end" ? "Select End Time" : "Select Start Time"}
        onSelectTime={(time24) => {
          if (activeTimeModal === "start") {
            setEditForm((prev) => ({ ...prev, start_time: time24 }));
          } else if (activeTimeModal === "end") {
            setEditForm((prev) => ({ ...prev, end_time: time24 }));
          }
          setActiveTimeModal(null);
        }}
      />
    </div>
  );
}
